import React, { useEffect, useReducer } from 'react';
import { Box, IconButton, Input, Text, VStack, Grid } from '@chakra-ui/react';
import { get } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';

import { IoInformationCircleOutline } from 'react-icons/io5';
import Constants from '../../../../Constants';
import { changeableKeyCreator } from '../../../../utils';
import SelectField from '../../../basic/SelectField';
import DateSection from '../DateSection/DateSection';
import InternationalOrderDetails from './InternationalOrderDetails/InternationalOrderDetails';
import { CFSwitch } from '../../../basic/Toggle/Toggle';
import CustomFormControl from '../../../core/CustomFormControl';
import DateTimeWithTrappedFocus from '../../../core/DateTimeWithTrappedFocus';
import { Tooltip } from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';

import 'top-loader-service/TopLoaderService.css';
import './EditInternationalOrder.scss';
import FastInput from '../../../basic/FastInput';
import { getTenantFromDomain } from '../../../../utils';
import { Alert } from '../../../ui/alert';


const tenant = getTenantFromDomain();

const OrderDetailsTab = props => {
  const [fieldErrors, setFieldErrors] = React.useState({});
  const {
    form,
    isInEditMode,
    requiredFieldsForm,
    officeOptions,
    productOptions,
    endUserOptionsFilteredByGrinder,
    endUserCurrent,
    packerOptions,
    allPackers,
    sortedPackerOptions,
    packerPlantOptionsByPacker,
    allPackerPlants,
    sortedPackerPlantOptionsByPacker,
    isEditableGrinderPoNumber,
    buyerOptions,
    allGrinder,
    isEUDomestic,
    isAUSDomestic,
    IsValidUSDomesticOrderFlag,
    containerOptions,
    isReadOnlyField,
    requiredFields,
    deliveryDateEditable,
    grinderDestinationCountry,
    hasAsianGrinder,
    hasUSAGrinder,
    isFrozenProduct,
    isUSADomestic,
    showUSADomesticFreshnessField,
    poHedgingEnabled,
    labelOptions,
    hasVoyageDetails,
    isColdstoreRequired,
    formFields,
    showShipment,
    dateValidationErrors,
    handleToggleChange,
    handleStartDateValueChange,
    onChangeValue,
    handleChangeValue,
    handleDateChange,
    onFXChangeValue,
    onFXRoundingDecimal,
    onBooleanValueChange,
    user,
    users,
  } = props;

  const initialDebouncedFields = {
    packerSoNumber: form.packerSoNumber || '',
    packerInvoiceNumber: form.packerInvoiceNumber || '',
    additinalGrn: form.additinalGrn || '',
    entrapment: form.entrapment || '',
    numberOfCartonsWithExposedMeat: form.cartonsExposedMeat || '', 
    numberOfCartonsWithLinerStuckToGlue: form.cartonsGlueStuckToLiner || '',
     };

  function debouncedFieldsReducer(state, action) {
    switch (action.type) {
      case 'SET_FIELD':
        return { ...state, [action.field]: action.value };
      case 'RESET':
        return { ...initialDebouncedFields, ...action.payload };
      default:
        return state;
    }
  }

  const [debouncedFields, dispatchDebouncedFields] = useReducer(debouncedFieldsReducer, initialDebouncedFields);

  useEffect(() => {
    dispatchDebouncedFields({
      type: 'RESET',
      payload: {
        packerSoNumber: form.packerSoNumber || '',
        packerInvoiceNumber: form.packerInvoiceNumber || '',
        additinalGrn: form.additinalGrn || '',
        entrapment: form.entrapment || '',
        numberOfCartonsWithExposedMeat: form.cartonsExposedMeat || '',
        numberOfCartonsWithLinerStuckToGlue: form.cartonsGlueStuckToLiner || '',
      },
    });
  }, [form.packerSoNumber, form.packerInvoiceNumber, form.additinalGrn, form.entrapment, form.cartonsExposedMeat, form.cartonsGlueStuckToLiner]);

  const getMaxAllowedValue = (rawSellQuantity, sellUOM) => {
    if (!rawSellQuantity || rawSellQuantity <= 0) {
      return 0;
    }
    const uomLower = sellUOM?.toLowerCase();
    if (uomLower === 'lbs' || uomLower === 'lb') {
      return rawSellQuantity / 60;
    }
    if (uomLower === 'kgs' || uomLower === 'kg') {
      return rawSellQuantity / 27.2;
    }
    return rawSellQuantity;
  };

  const handleDebouncedFieldChange = field => e => {
    let value = e.target.value;
    const maxAllowedValue = getMaxAllowedValue(form.sellQuantity, form.sellUnitOfMeasure?.name);
    console.log(maxAllowedValue)

    if (
      ['entrapment', 'numberOfCartonsWithExposedMeat', 'numberOfCartonsWithLinerStuckToGlue'].includes(field) &&
      Number(value) > maxAllowedValue
    ) {
      const fieldLabels = {
        entrapment: 'Number of cartons entrapped',
        numberOfCartonsWithExposedMeat: 'Number of Cartons with Exposed Meat',
        numberOfCartonsWithLinerStuckToGlue: 'Number of Cartons with Liner Stuck to Glue',
      };
      setFieldErrors(prev => ({
        ...prev,
        [field]: `More than Load Total Carton Count not allowed`,
      }));
      return;
    }

    setFieldErrors(prev => ({
      ...prev,
      [field]: null,
    }));

    dispatchDebouncedFields({
      type: 'SET_FIELD',
      field,
      value,
    });
  };


  const prevDebouncedFieldsRef = React.useRef(debouncedFields);

  useEffect(() => {
    const changedFields = Object.entries(debouncedFields).filter(
      ([field, value]) => prevDebouncedFieldsRef.current[field] !== value
    );

    const timeouts = changedFields.map(([field, value]) =>
      setTimeout(() => {
        // Convert empty strings to null for numeric fields, otherwise convert to number
        let finalValue = value;
        if (['entrapment', 'cartons_exposed_meat', 'cartons_glue_stuck_to_liner'].includes(field)) {
          if (value === '') {
            finalValue = null;
          } else {
            finalValue = Number(value);
          }
        }
        onChangeValue(field, finalValue);
      }, 500)
    );

    prevDebouncedFieldsRef.current = debouncedFields;

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [debouncedFields, onChangeValue]);

  return (
    <VStack align="stretch">
      <Grid gap="18px" templateColumns="repeat(4, 1fr)" width="100%">
        {form.status === Constants.ORDER_STATUSES.DRAFT && (
          <>
            <SelectField
              label={
                <>
                  Office&nbsp;
                  <Tooltip
                    content="Specifies the office managing the order for coordination."
                    positioning={{ placement: 'right-end' }}
                  >
                    <IconButton
                      width="14px"
                      height="14px"
                      padding="0"
                      minW="auto"
                      borderRadius="50%"
                      color="#878787"
                      variant="unstyled"
                    >
                      <IoInformationCircleOutline size="14px" />
                    </IconButton>
                  </Tooltip>
                </>
              }
              id="select-purchasingOffice"
              name="purchasingOffice"
              options={officeOptions?.sort((a, b) => (a.label > b.label ? 1 : -1))}
              placeholder="Purchasing Office"
              disabled={!isInEditMode}
              value={officeOptions.find(office => office.value === form.purchasingOffice)}
              className={requiredFieldsForm.includes('purchasingOffice') ? 'error' : ''}
              onChange={e => onChangeValue('purchasingOffice', e)}
            />

            <SelectField
              label={
                <>
                  Product Type&nbsp;
                  <Tooltip
                    content="Specifies whether the order is for Beef or Pork to categorize the meat product."
                    positioning={{ placement: 'right-end' }}
                  >
                    <IconButton
                      width="14px"
                      height="14px"
                      padding="0"
                      minW="auto"
                      borderRadius="50%"
                      color="#878787"
                      variant="unstyled"
                    >
                      <IoInformationCircleOutline size="14px" />
                    </IconButton>
                  </Tooltip>
                </>
              }
              id="select-product-type"
              name="productType"
              options={productOptions?.sort((a, b) => (a.label > b.label ? 1 : -1))}
              placeholder="Product Type"
              disabled={!isInEditMode}
              value={productOptions?.find(productType => productType?.value === form?.productType) || null}
              className={requiredFieldsForm.includes('productType') ? 'error' : ''}
              onChange={e => onChangeValue('productType', e)}
            />
          </>
        )}

        <CustomFormControl
          key="CustomFormControlGrinderPO"
          changeableKey={changeableKeyCreator([isInEditMode, form.grinderPoNumber])}
          label={
            <>
              Grinder PO#&nbsp;
              <Tooltip
                content="Displays the purchase order number assigned by the grinder."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
        >
          <FastInput
            id="grinderPO"
            name="grinderPoNumber"
            type="text"
            className="form-control"
            disabled={!isInEditMode || !isEditableGrinderPoNumber}
            value={form.grinderPoNumber || ''}
            onChange={value => onChangeValue('grinderPoNumber', { value, currentTarget: { value }, target: { value } })}
          />
        </CustomFormControl>

        <SelectField
          label={
            <>
              End User&nbsp;
              <Tooltip
                content="Identifies the final recipient to ensure the product meets specific requirements."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
          id="select-end_user"
          name="endUser"
          options={endUserOptionsFilteredByGrinder?.sort((a, b) => (a.label > b.label ? 1 : -1))}
          placeholder="End User"
          disabled={!isInEditMode}
          value={endUserCurrent}
          className={requiredFieldsForm.includes('endUser') ? 'error' : ''}
          onChange={e => onChangeValue('endUser', e)}
        />

        <SelectField
          label={
            <>
              Packer&nbsp;
              <Tooltip
                content="Specifies the packer responsible for handling and tracking."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
          id="select-packer"
          name="packerId"
          value={
            (isInEditMode ? packerOptions : (allPackers || []).map(el => ({ value: el?.id, label: el?.name }))).find(
              option => option.value === get(form, 'packerId')
            ) || null
          }
          options={sortedPackerOptions}
          disabled={!isInEditMode}
          className={requiredFieldsForm.includes('packerId') ? 'error' : ''}
          onChange={e => onChangeValue('packerId', e ? e.value : '')}
        />

        <SelectField
          label={
            <>
              Establishment&nbsp;
              <Tooltip
                content="Identifies the establishment associated with the order."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
          id="select-est"
          name="packerPlant"
          value={
            (isInEditMode
              ? packerPlantOptionsByPacker
              : (allPackerPlants || []).map(el => ({ value: el?.id, label: el?.name }))
            ).find(option => option.value === get(form, 'packerPlantId')) || null
          }
          options={sortedPackerPlantOptionsByPacker}
          disabled={!isInEditMode}
          className={requiredFieldsForm.includes('packerPlantId') ? 'error' : ''}
          onChange={e => onChangeValue('packerPlant', e)}
        />

        <CustomFormControl
          key="CustomFormControlEstId"
          changeableKey={changeableKeyCreator([isInEditMode, get(form, 'estId')])}
          label={
            <>
              Establishment Number&nbsp;
              <Tooltip
                content="Records the official number for traceability and compliance."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
        >
          <FastInput
            id="estId"
            name="estId"
            value={
              isInEditMode && allPackerPlants?.find(option => option.id === get(form, 'packerPlantId'))?.delisted
                ? ''
                : get(form, 'estId')
            }
            disabled={!isInEditMode}
            className={requiredFieldsForm.includes('estId') ? 'error' : ''}
            onChange={value => handleChangeValue({ target: { value, name: 'estId' } })}
          />
        </CustomFormControl>
        {/* <!-- BUYER --> */}

        <SelectField
          label={
            <>
              Buyer&nbsp;
              <Tooltip
                content="Identifies the individual or organization purchasing the product."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
          id="select-buyer"
          name="buyerId"
          value={buyerOptions.find(option => option.value === get(form, 'buyerId')) || null}
          options={buyerOptions}
          disabled={!isInEditMode}
          placeholder="Salesman"
          className={requiredFieldsForm.includes('buyerId') ? 'error' : ''}
          onChange={e => onChangeValue('buyerId', e)}
          isClearable={false}
        />

        {(isEUDomestic || isAUSDomestic || IsValidUSDomesticOrderFlag) && (
          <>
            <SelectField
              label={
                <>
                  Container Type&nbsp;
                  <Tooltip
                    content="Specifies the type of container used for shipping."
                    positioning={{ placement: 'right-end' }}
                  >
                    <IconButton
                      width="14px"
                      height="14px"
                      padding="0"
                      minW="auto"
                      borderRadius="50%"
                      color="#878787"
                      variant="unstyled"
                    >
                      <IoInformationCircleOutline size="14px" />
                    </IconButton>
                  </Tooltip>
                </>
              }
              id="container-type"
              name="loadContainerDetails"
              defaultValue={form?.loadContainerDetails}
              options={containerOptions}
              disabled={!isInEditMode}
              inputProps={{
                className: 'm-input',
                disabled: !isInEditMode,
              }}
              placeholder="Container/Load Combo"
              className={requiredFieldsForm.includes('loadContainerDetails') ? 'error' : ''}
              onChange={e => onChangeValue('loadContainerDetails', e)}
            />
            {/* Removed trucking company as per task #4716 Requirement */}
            <CustomFormControl
              key="CustomFormControlDeliveryDate"
              changeableKey={changeableKeyCreator([isInEditMode, form.deliveryDate])}
            >
              <Text as="label" htmlFor="delivery-date">
                {isReadOnlyField ? 'Delivery Date (add it in the inventory)' : 'Delivery Date'}
                &nbsp;
                <Tooltip
                  content="Indicates the date when the shipment is expected to be delivered."
                  positioning={{ placement: 'right-end' }}
                >
                  <IconButton
                    width="14px"
                    height="14px"
                    padding="0"
                    minW="auto"
                    borderRadius="50%"
                    color="#878787"
                    variant="unstyled"
                  >
                    <IoInformationCircleOutline size="14px" />
                  </IconButton>
                </Tooltip>
              </Text>
              <DateTimeWithTrappedFocus
                id="delivery-date"
                name="deliveryDate"
                inputProps={{
                  className: 'form-control m-input',
                  disabled: !isInEditMode || isReadOnlyField,
                }}
                closeOnSelect
                value={form.deliveryDate}
                onChange={e => {
                  // eslint-disable-next-line no-underscore-dangle
                  handleDateChange('deliveryDate', e?._isAMomentObject ? e?.format('YYYY-MM-DD') : e);
                }}
                className={requiredFields?.po?.includes('deliveryDate') ? 'error' : ''}
                timeFormat={false}
                dateFormat="YYYY-MM-DD"
              />
            </CustomFormControl>
            <Box>
              <label htmlFor="delivery_time">
                Delivery Time&nbsp;
                <Tooltip
                  content="Captures the expected delivery time for scheduling and coordination."
                  positioning={{ placement: 'right-end' }}
                >
                  <IconButton
                    width="14px"
                    height="14px"
                    padding="0"
                    minW="auto"
                    borderRadius="50%"
                    color="#878787"
                    variant="unstyled"
                  >
                    <IoInformationCircleOutline size="14px" />
                  </IconButton>
                </Tooltip>
              </label>
              <Input
                disabled={!isInEditMode || isReadOnlyField}
                type="time"
                id="delivery_time"
                value={form.scheduledDeliveryTime}
                onChange={value => {
                  const timeValue = moment(value.target.value, 'HH:mm');
                  handleDateChange('scheduledDeliveryTime', timeValue && timeValue.format('HH:mm'));
                }}
              />
            </Box>
            <CustomFormControl
              key="CustomFormControlADeliveryDate"
              changeableKey={changeableKeyCreator([isInEditMode, form.recvDeliveryDate])}
              label={
                <>
                  Actual Delivery Date&nbsp;
                  <Tooltip
                    content="Captures the actual delivery time for scheduling and coordination."
                    positioning={{ placement: 'right-end' }}
                  >
                    <IconButton
                      width="14px"
                      height="14px"
                      padding="0"
                      minW="auto"
                      borderRadius="50%"
                      color="#878787"
                      variant="unstyled"
                    >
                      <IoInformationCircleOutline size="14px" />
                    </IconButton>
                  </Tooltip>
                </>
              }
            >
              <DateTimeWithTrappedFocus
                id="actual-delivery-date"
                inputProps={{
                  className: 'form-control m-input',
                  disabled: !isInEditMode || !deliveryDateEditable,
                }}
                closeOnSelect
                value={form.recvDeliveryDate}
                onChange={e => {
                  // eslint-disable-next-line no-underscore-dangle
                  handleDateChange('recvDeliveryDate', e?._isAMomentObject ? e?.format('YYYY-MM-DD') : e);
                }}
                timeFormat={false}
                dateFormat="YYYY-MM-DD"
              />
            </CustomFormControl>
          </>
        )}
        {isFrozenProduct && isUSADomestic && showUSADomesticFreshnessField && (
          <CustomFormControl
            key="CustomFormControlFrozenOrderType"
            changeableKey={changeableKeyCreator([isInEditMode, form.frozenOrderType])}
            label="Type"
          >
            <CFSwitch
              // onClick={value => onToggleChange(value ? 'arrival' : 'shipment')}
              onText="Frozen Coldstore Domestic"
              disabled={!isInEditMode}
              offText="Frozen Domestic"
              // isActive={!showShipment}
              style={{ borderRadius: '10px !important' }}
            />
          </CustomFormControl>
        )}
        <CustomFormControl
          key="CustomFormControlPackerPaidDate"
          changeableKey={changeableKeyCreator([isInEditMode, form.packerPaidDate])}
          label={
            <>
              Packer Paid Date&nbsp;
              <Tooltip
                content="Displays the date when the packer made the payment."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
        >
          <DateTimeWithTrappedFocus
            id="packerPaidDate"
            inputProps={{
              className: 'form-control m-input',
              disabled: !isInEditMode,
            }}
            closeOnSelect
            value={form.packerPaidDate}
            onChange={e => {
              // eslint-disable-next-line no-underscore-dangle
              handleDateChange('packerPaidDate', e?._isAMomentObject ? e?.format('YYYY-MM-DD') : e);
            }}
            timeFormat={false}
            dateFormat="YYYY-MM-DD"
          />
        </CustomFormControl>
        {grinderDestinationCountry && hasAsianGrinder && (
          <CustomFormControl
            key="CustomFormControlPreferredEta"
            changeableKey={changeableKeyCreator([isInEditMode, form.preferredEta])}
            label="Preferred ETA"
          >
            <DateTimeWithTrappedFocus
              id="preferredEta"
              inputProps={{
                className: 'form-control m-input',
                disabled: !isInEditMode,
              }}
              closeOnSelect
              value={form.preferredEta || null}
              // eslint-disable-next-line no-underscore-dangle
              onChange={e => onChangeValue('preferredEta', e?._isAMomentObject ? e?.format('YYYY-MM-DD') : e)}
              timeFormat={false}
              dateFormat="YYYY-MM-DD"
            />
          </CustomFormControl>
        )}
        <CustomFormControl
          key="CustomFormControlPackerInvoiceNumber"
          //   changeableKey={changeableKeyCreator([isInEditMode, form.packerInvoiceNumber])}
          label={
            <>
              Packer Invoice #&nbsp;
              <Tooltip
                content="Displays the invoice number assigned by the packer."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
        >
          <Input
            name="packerInvoiceNumber"
            type="text"
            disabled={!isInEditMode}
            value={debouncedFields.packerInvoiceNumber || ''}
            onChange={e => handleDebouncedFieldChange('packerInvoiceNumber')(e)}
          />
        </CustomFormControl>
        <CustomFormControl
          key="CustomFormControlPackerInvoiceDate"
          changeableKey={changeableKeyCreator([isInEditMode, form.packerInvoiceDate])}
          label={
            <>
              Packer Invoice Date&nbsp;
              <Tooltip
                content="Displays the date when the packer made the payment."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
        >
          <DateTimeWithTrappedFocus
            id="packerInvoiceDate"
            inputProps={{
              className: 'form-control m-input',
              disabled: !isInEditMode,
            }}
            closeOnSelect
            value={form.packerInvoiceDate}
            onChange={e => {
              // eslint-disable-next-line no-underscore-dangle
              handleDateChange('packerInvoiceDate', e?._isAMomentObject ? e?.format('YYYY-MM-DD') : e);
            }}
            timeFormat={false}
            dateFormat="YYYY-MM-DD"
          />
        </CustomFormControl>

        <CustomFormControl
          key="CustomFormControlAPackerSoNumber"
          //   changeableKey={changeableKeyCreator([isInEditMode, form.packerSoNumber])}
          label={
            <>
              Packer SO #&nbsp;
              <Tooltip
                content="Displays the sales order number assigned by the packer."
                positioning={{ placement: 'right-end' }}
              >
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
        >
          <Input
            id="packerSoNumber"
            name="packerSoNumber"
            type="text"
            disabled={!isInEditMode}
            value={debouncedFields.packerSoNumber || ''}
            onChange={e => handleDebouncedFieldChange('packerSoNumber')(e)}
          />
        </CustomFormControl>
        {isFrozenProduct && isUSADomestic ? (
          <>
            <CustomFormControl
              key="CustomFormControlColdstorePlannedDeliveryDate"
              changeableKey={changeableKeyCreator([isInEditMode, form?.coldstorePlannedDeliveryDate])}
              label={
                <>
                  Planned Delivery Date to Coldstore&nbsp;
                  <Tooltip
                    content="Displays the planned delivery date to the cold storage."
                    positioning={{ placement: 'right-end' }}
                  >
                    <IconButton
                      width="14px"
                      height="14px"
                      padding="0"
                      minW="auto"
                      borderRadius="50%"
                      color="#878787"
                      variant="unstyled"
                    >
                      <IoInformationCircleOutline size="14px" />
                    </IconButton>
                  </Tooltip>
                </>
              }
            >
              <DateTimeWithTrappedFocus
                name="coldstorePlannedDeliveryDate"
                value={form?.coldstorePlannedDeliveryDate}
                inputProps={{
                  className: 'form-control m-input',
                  disabled: !isInEditMode,
                }}
                closeOnSelect
                onChange={e => {
                  // eslint-disable-next-line no-underscore-dangle
                  handleDateChange('coldstorePlannedDeliveryDate', e?._isAMomentObject ? e?.format('YYYY-MM-DD') : e);
                }}
                timeFormat={false}
                dateFormat="YYYY-MM-DD"
              />
            </CustomFormControl>
          </>
        ) : (
          ''
        )}
        {(hasVoyageDetails || isColdstoreRequired) && (
          <DateSection
            {...{
              orderDate: props.currentOrder?.orderDate,
              arrivalDateStart: form.arrivalDateStart,
              arrivalDateEnd: form.arrivalDateEnd,
              shipmentDateStart: form.shipmentDateStart,
              shipmentDateEnd: form.shipmentDateEnd,
              hasAsianGrinder,
              hasUSAGrinder,
              loadContainerDetails: form.loadContainerDetails,
              showShipment,
              onToggleChange: handleToggleChange,
              // onDateChange: handleStartDateValueChange,
              onDateChange: (title,key,value) => {handleStartDateValueChange(title,key,value,grinderDestinationCountry)},
              onChangeFieldValue: onChangeValue,
              requiredFields,
              isInEditMode,
              isShipmentDateRequiredPrompt: form.isShipmentDateRequiredPrompt,
            }}
          />
        )}
        {dateValidationErrors?.errorMsg?.text && dateValidationErrors.errorMsg.tab === 'orderDetails' && (
          <Alert status = "error" title = {dateValidationErrors.errorMsg.text}/>
        )}
        {formFields?.find(field => field?.type === 'show_estimated_delivery_date' && field?.value === '1') &&
          !isUSADomestic &&
          !isAUSDomestic && (
            <>
              <CustomFormControl
                key="CustomFormControlEstimatedDeliveryDate"
                changeableKey={changeableKeyCreator([isInEditMode, form?.estimatedDeliveryDate])}
                label="Estimated Delivery Date"
              >
                <DateTimeWithTrappedFocus
                  name="estimatedDeliveryDate"
                  value={form?.estimatedDeliveryDate}
                  inputProps={{
                    className: 'form-control m-input',
                    disabled: !isInEditMode,
                  }}
                  closeOnSelect
                  onChange={e => {
                    // eslint-disable-next-line no-underscore-dangle
                    handleDateChange('estimatedDeliveryDate', e?._isAMomentObject ? e?.format('YYYY-MM-DD') : e);
                  }}
                  timeFormat={false}
                  dateFormat="YYYY-MM-DD"
                />
              </CustomFormControl>
            </>
          )}
        {isFrozenProduct && isUSADomestic && showUSADomesticFreshnessField && (
          <CustomFormControl
            key="CustomFormControlIsStanding"
            changeableKey={changeableKeyCreator([isInEditMode, form.isStanding])}
          >
            <Checkbox
              name="isStanding"
              colorScheme="actionSecondary"
              onChange={handleChangeValue}
              disabled={!isInEditMode}
            >
              Standing
            </Checkbox>
          </CustomFormControl>
        )}
        {poHedgingEnabled && (
          <CustomFormControl
            key="CustomFormControlHedgeContractNumber"
            changeableKey={changeableKeyCreator([isInEditMode, form.hedgeContractNumber])}
            label="Hedge Contract Number"
          >
            <FastInput
              id="hedgeContractNumber"
              name="hedgeContractNumber"
              type="text"
              disabled={!isInEditMode}
              value={form.hedgeContractNumber || ''}
              onChange={value => handleChangeValue({ target: { value, name: 'hedgeContractNumber' } })}
            />
          </CustomFormControl>
        )}

        <SelectField
          label={
            <>
              Tags&nbsp;
              <Tooltip content="Displays the tags added for the order." positioning={{ placement: 'right-end' }}>
                <IconButton
                  width="14px"
                  height="14px"
                  padding="0"
                  minW="auto"
                  borderRadius="50%"
                  color="#878787"
                  variant="unstyled"
                >
                  <IoInformationCircleOutline size="14px" />
                </IconButton>
              </Tooltip>
            </>
          }
          {...{
            className: 'edit-international-order__select',
            isClearable: true,
            disabled: !isInEditMode,
            isSearchable: true,
            closeMenuOnSelect: false,
            removeSelected: true,
            isMulti: true,
            name: 'select-tags',
            value: form.tags ? form.tags.map(t => ({ label: t, value: t })) : [],
            onChange: e => onChangeValue('tags', e && e.length ? e.map(lbl => lbl.value) : []),
            options: labelOptions?.sort((a, b) => (a.label > b.label ? 1 : -1)),
          }}
        />
        {isFrozenProduct && isUSADomestic && (
          <CustomFormControl
            key="CustomFormControlPriceType"
            changeableKey={changeableKeyCreator([isInEditMode, form.priceType])}
            label={
              <>
                Order Type&nbsp;
                <Tooltip
                  content="Specifies the nature of the order (Spot, Contract, Formula, Other, NOF)."
                  positioning={{ placement: 'right-end' }}
                >
                  <IconButton
                    width="14px"
                    height="14px"
                    padding="0"
                    minW="auto"
                    borderRadius="50%"
                    color="#878787"
                    variant="unstyled"
                  >
                    <IoInformationCircleOutline size="14px" />
                  </IconButton>
                </Tooltip>
              </>
            }
          >
            <Input id="orderType" type="text" disabled value={form.priceType || ''} />
          </CustomFormControl>
        )}

        <CustomFormControl
          key="CustomFormControladditinalGrn"
          //   changeableKey={changeableKeyCreator([isInEditMode, form.additinalGrn])}
          label="Additional Reference"
        >
          <Input
            id="additinalGrn"
            name="additinalGrn"
            type="text"
            disabled={!isInEditMode}
            value={debouncedFields.additinalGrn || ''}
            onChange={e => handleDebouncedFieldChange('additinalGrn')(e)}
          />
        </CustomFormControl>
        <CustomFormControl
          key="CustomFormControlentrapment"
          //   changeableKey={changeableKeyCreator([isInEditMode, form.entrapment])}
          label="Entrapment"
        >
          <Input
            id="entrapment"
            name="entrapment"
            type="number"
            disabled={!isInEditMode || form?.sellUnitOfMeasure?.name === 'bin'}
            value={debouncedFields.entrapment || ''}
            onChange={e => handleDebouncedFieldChange('entrapment')(e)}
          />
          {fieldErrors.entrapment && (
            <Text color="red.500" fontSize="sm" mt="2">
              {fieldErrors.entrapment}
            </Text>
          )}
        </CustomFormControl>
      </Grid>
      {tenant !== 'cfc' && (
        <Grid gap="18px" templateColumns="repeat(4, 1fr)" width="100%">
          <CustomFormControl
            key="CustomFormControlNumberOfCartonsWithExposedMeat"
            label="Number of Cartons with Exposed Meat"
          >
            <Input
              id="numberOfCartonsWithExposedMeat"
              name="numberOfCartonsWithExposedMeat"
              type="number"
              disabled={!isInEditMode}
              value={debouncedFields.numberOfCartonsWithExposedMeat || ''}
              onChange={e => handleDebouncedFieldChange('numberOfCartonsWithExposedMeat')(e)}
              borderColor={fieldErrors.numberOfCartonsWithExposedMeat ? 'red.500' : undefined}
            />
            {fieldErrors.numberOfCartonsWithExposedMeat && (
              <Text color="red.500" fontSize="sm" mt="2">
                {fieldErrors.numberOfCartonsWithExposedMeat}
              </Text>
            )}
          </CustomFormControl>
          <CustomFormControl
            key="CustomFormControlNumberOfCartonsWithLinerStuckToGlue"
            label="Number of Cartons with Liner Stuck to Glue"
          >
            <Input
              id="numberOfCartonsWithLinerStuckToGlue"
              name="numberOfCartonsWithLinerStuckToGlue"
              type="number"
              disabled={!isInEditMode}
              value={debouncedFields.numberOfCartonsWithLinerStuckToGlue || ''}
              onChange={e => handleDebouncedFieldChange('numberOfCartonsWithLinerStuckToGlue')(e)}
              borderColor={fieldErrors.numberOfCartonsWithLinerStuckToGlue ? 'red.500' : undefined}
            />
            {fieldErrors.numberOfCartonsWithLinerStuckToGlue && (
              <Text color="red.500" fontSize="sm" mt="2">
                {fieldErrors.numberOfCartonsWithLinerStuckToGlue}
              </Text>
            )}
          </CustomFormControl>
        </Grid>
      )}
      <Grid gap="18px" templateColumns="repeat(4, 1fr)" width="100%">
      </Grid>
      <InternationalOrderDetails
        onFXChangeValue={onFXChangeValue}
        onFXRoundingDecimal={onFXRoundingDecimal}
        onBooleanValueChange={onBooleanValueChange}
        form={form}
        requiredFields={requiredFields}
        editMode={isInEditMode}
        user={user}
        users={users}
        showToggles={!(isFrozenProduct && isUSADomestic)}
        {...props}
        handleChangeValue={(name, e) => handleChangeValue({ target: { name, ...e } })}
      />
    </VStack>
  );
};

OrderDetailsTab.propTypes = {
  form: PropTypes.shape({
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entrapment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    numberOfCartonsWithExposedMeat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    numberOfCartonsWithLinerStuckToGlue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    additinalGrn: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sellUnitOfMeasure: PropTypes.shape({
      name: PropTypes.string,
    }),
    priceType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tags: PropTypes.arrayOf(PropTypes.string),
    hedgeContractNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isStanding: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
    estimatedDeliveryDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
    isShipmentDateRequiredPrompt: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
    purchasingOffice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    grinderPoNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    endUser: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    packerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    packerPlantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    estId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    buyerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    loadContainerDetails: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deliveryDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    scheduledDeliveryTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recvDeliveryDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shipmentDateStart: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shipmentDateEnd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    arrivalDateStart: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    arrivalDateEnd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    coldstorePlannedDeliveryDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    preferredEta: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    packerPaidDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    packerInvoiceNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    packerInvoiceDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    packerSoNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    frozenOrderType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  isInEditMode: PropTypes.bool,
  requiredFieldsForm: PropTypes.arrayOf(PropTypes.string),
  officeOptions: PropTypes.arrayOf(PropTypes.shape({})),
  productOptions: PropTypes.arrayOf(PropTypes.shape({})),
  endUserOptionsFilteredByGrinder: PropTypes.arrayOf(PropTypes.shape({})),
  endUserCurrent: PropTypes.shape({}),
  packerOptions: PropTypes.arrayOf(PropTypes.shape({})),
  allPackers: PropTypes.arrayOf(PropTypes.shape({})),
  sortedPackerOptions: PropTypes.arrayOf(PropTypes.shape({})),
  packerPlantOptionsByPacker: PropTypes.arrayOf(PropTypes.shape({})),
  allPackerPlants: PropTypes.arrayOf(PropTypes.shape({})),
  sortedPackerPlantOptionsByPacker: PropTypes.arrayOf(PropTypes.shape({})),
  isEditableGrinderPoNumber: PropTypes.bool,
  buyerOptions: PropTypes.arrayOf(PropTypes.shape({})),
  isEUDomestic: PropTypes.bool,
  isAUSDomestic: PropTypes.bool,
  IsValidUSDomesticOrderFlag: PropTypes.bool,
  containerOptions: PropTypes.arrayOf(PropTypes.shape({})),
  isReadOnlyField: PropTypes.bool,
  requiredFields: PropTypes.shape({
    po: PropTypes.arrayOf(PropTypes.string),
  }),
  transporterValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({})]),
  transporterOptions: PropTypes.arrayOf(PropTypes.shape({})),
  deliveryDateEditable: PropTypes.bool,
  grinderDestinationCountry: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  hasAsianGrinder: PropTypes.bool,
  hasUSAGrinder: PropTypes.bool,
  isFrozenProduct: PropTypes.bool,
  isUSADomestic: PropTypes.bool,
  showUSADomesticFreshnessField: PropTypes.bool,
  poHedgingEnabled: PropTypes.bool,
  labelOptions: PropTypes.arrayOf(PropTypes.shape({})),
  hasVoyageDetails: PropTypes.bool,
  isColdstoreRequired: PropTypes.bool,
  formFields: PropTypes.arrayOf(PropTypes.shape({})),
  showShipment: PropTypes.bool,
  handleToggleChange: PropTypes.func,
  handleStartDateValueChange: PropTypes.func,
  onChangeValue: PropTypes.func,
  handleChangeValue: PropTypes.func,
  handleDateChange: PropTypes.func,
  onFXChangeValue: PropTypes.func,
  onFXRoundingDecimal: PropTypes.func,
  onBooleanValueChange: PropTypes.func,
  user: PropTypes.shape({}),
  users: PropTypes.arrayOf(PropTypes.shape({})),
};

export default OrderDetailsTab;
